import { Box, Button, Card, CircularProgress, FormControl, InputLabel, MenuItem, Modal, Select, TextField } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routes } from '@frontend/router/routes';
import { useGetAllAgencies } from '@frontend/features/agencies/queries';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useGetClientById } from '@frontend/features/clients/queries';
import { OriginatedByChannel, UserRoles } from '@bandido/shared/types';
import { AgencyResponseDto } from '@bandido/shared/dtos';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clientId?: string;
}

export const CaseCreateModal = ({ isOpen, onClose, clientId }: Props) => {
  const { addToast } = useSnackBar();
  const [value, setValue] = useState('');
  const [creating, setCreating] = useState(false);
  const { ability, myProfile, userAgencies } = useAuth();
  const [agencyId, setAgencyId] = useState<number | undefined>();
  const [agencyDirty, setAgencyDirty] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);
  const [finalAgencies, setFinalAgencies] = useState<AgencyResponseDto[]>([]);
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const handlerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const { data: agencies } = useGetAllAgencies({
    pageSize: 10000,
    currentPage: 1,
    search: '',
  });

  const { data: clientData } = useGetClientById({
    id: clientId,
  });

  const canChangeAgency = ability && ability.can('view', 'AgencySelector');

  const handleSubmit = useCallback(
    (event: { preventDefault: () => void }) => {
      event.preventDefault();
      const submit = async () => {
        try {
          const finalAgencyId = canChangeAgency ? agencyId : myProfile?.agencyId;
          const request = selectedClientId
            ? { name: value, agencyId: finalAgencyId, clientId: selectedClientId }
            : { name: value, agencyId: finalAgencyId };

          const result = await client.post('/case', { ...request, originatedByChannel: OriginatedByChannel.MANUAL });

          addToast({
            message: t('cases.create.createdSuccess'),
            color: 'success',
          });

          navigate(routes.dashboard.caseView.replace(':id', result.data.id));
          setValue('');
          handleClose();
        } catch (_e) {
          addToast({
            message: t('cases.create.createdError'),
            color: 'error',
          });
        }
        finally {
          setCreating(false);
        }
      };
      setCreating(true);
      if (handlerRef.current) {
        clearTimeout(handlerRef.current);
      }
      handlerRef.current = setTimeout(() => {
        submit();
      }, 300);
    },
    [value, canChangeAgency, myProfile?.agencyId, selectedClientId, agencyId],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAgencyDirty(true);
    setValue(e.target.value);
  };

  const handleClose = () => {
    setAgencyDirty(false);
    onClose();
  };

  useEffect(() => {
    if (agencies) {
      if (myProfile?.roles.includes(UserRoles.ADMINS)) {
        setFinalAgencies(agencies.items);
      } else {
        setFinalAgencies(agencies.items.filter((a) => userAgencies?.includes(a.id)));
      }
    }
  }, [agencies, userAgencies, myProfile]);

  useEffect(() => {
    if (clientId) {
      setSelectedClientId(clientId);
      
      // Si tenemos los datos del cliente, usar su agencyId
      if (clientData && agencies && !agencyDirty) {
        setAgencyId(clientData.agencyId);
      }
    } else {
      setSelectedClientId(undefined);
      if (agencies && agencies.items[0]?.id !== agencyId && !agencyDirty) {
        setAgencyId(agencies.items[0]?.id);
      }
    }
  }, [clientId, clientData, agencies, agencyId, agencyDirty]);

  useEffect(() => () => {
    if (handlerRef.current) {
      clearTimeout(handlerRef.current);
    }
  }, []);

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: 'calc(100% - 32px)', md: '80%' },
          maxWidth: 800,
          p: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          '& input, & .MuiSelect-select': { fontSize: { xs: 16, md: 'inherit' } },
        }}
        elevation={4}
      >
        <Box ref={formRef} component="form" onSubmit={handleSubmit} gap={2} flex={1}>
          <TextField
            fullWidth
            autoFocus
            disabled={creating}
            size="small"
            placeholder={t('cases.create.placeholder')}
            onChange={handleChange}
          />
        </Box>
        <FormControl size="small" sx={{ width: { xs: '100%', md: 250 } }}>
          <InputLabel id="agency-selector">{t('cases.create.agency')}</InputLabel>
          <Select
            labelId="agency-selector"
            disabled={!canChangeAgency || creating}
            id="agency-selector"
            value={agencyId}
            onChange={(e) => {
              setAgencyDirty(true);
              setAgencyId(+e.target.value || undefined);
            }}
            fullWidth
          >
            {finalAgencies?.map((value) => (
              <MenuItem key={value.id} value={value.id}>
                {value.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} disabled={creating} sx={{ minHeight: 44 }}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={creating || !value.trim()} variant="contained" sx={{ minHeight: 44 }}>
            {t('common.create')}
          </Button>
        </Box>
        {
          creating && <CircularProgress size={20} />
        }
      </Card>
    </Modal>
  );
};
